import React, { useState, useEffect } from "react";
import Head from "next/head";
import {useRouter, useParams, useSearchParams} from "next/navigation";

import {
  Box,
  Divider,
  Typography,
  Button,
  Skeleton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import SocialMediaLinks from "../../SocialMediaLinks";
import EventAccordion from "../Events/EventAccordion";
import {
  useFetchBandByInternalLink,
  useFetchEventsByBandId,
} from "../../../hooks";
import ImageComponent from "../../Elements/ImageComponent";

const BandDetailsPage = () => {
  const { internalLink } = useParams();
  const router = useRouter();
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [localPagination, setLocalPagination] = useState({ skip: 0, take: 20 });
  const [currentPage, setCurrentPage] = useState(0);

  // Fetch the band details using a custom hook
  const { band, loading: bandLoading } =
    useFetchBandByInternalLink(internalLink);
  // Fetch the events associated with the band
  const {
    events,
    loading: eventsLoading,
    totalItems,
  } = useFetchEventsByBandId(band?.id, showPastEvents);

  const handleBack = () => {
    router.back(); // Navigate to the previous page in the history stack
  };
  const getPaginatedResults = () => {
    let searchResults = events.slice(
      localPagination.skip,
      localPagination.take + localPagination.skip - 1
    );

    return searchResults;
  };
  if (bandLoading) {
    return (
      <>
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
      </>
    );
  }

  if (!band) {
    return <Typography variant="h6">Band not found</Typography>;
  }

  return (
    <Box
      sx={{
        padding: { xs: 0, sm: 3 },
        margin: "0 auto",
      }}
    >
      <Head>
        <meta name="keywords" content={`${band?.bandname} Live Music Schedule`} />
        <meta name="description" content={`${band?.bandname} Live local music shows and concerts schedule near me`} />
        <meta property="og:title" content={`${band?.bandname} Live Music Schedule`} />
        <meta property="og:description" content={`${band?.bandname} Live local music shows and concerts schedule near me`} />
        <title>{`${band?.bandname} Live Music Schedule`}</title>
      </Head>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Button onClick={handleBack} variant="contained">
          Back
        </Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: "bold",
            textAlign: { xs: "center", sm: "left" },
            flex: 1,
          }}
        >
          {band?.bandname}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // Stack on small screens
          gap: 3,
        }}
      >
        {/* Left Side: Band Details */}
        <Box
          sx={{
            flex: 1,
            maxWidth: { md: "25%" }, // Limit width to 25% on medium and larger screens
            width: "100%",
          }}
        >
          <ImageComponent imageUrl={band?.imageUrl} altText={band?.bandname} />

          <Typography variant="h6" sx={{ mb: 3 }}>
            {band?.alias?.length ? (
              <>
                {"aka: "}
                <strong>{band?.alias?.map((a) => a).join(", ")}</strong>
              </>
            ) : null}
          </Typography>

          <Box sx={{ display: "flex", mb: 3 }}>
            <SocialMediaLinks
              size={"48"}
              links={band?.socialMediaLinks || []}
            />
          </Box>
          {band?.description && (
            <div>
              {band.description
                .trim() // remove any leading or trailing spaces/newlines
                .split("\n\n") // split by double newlines into paragraphs
                .map((paragraph, index) => (
                  <p key={index}>{paragraph}</p> // render each paragraph inside a <p> tag
                ))}
            </div>
          )}
        </Box>

        {/* Divider */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{ mx: 2, display: { xs: "none", md: "block" } }}
        />

        {/* Right Side: Events */}
        <Box
          sx={{
            flex: 3, // Ensure it takes up the remaining space
            width: "100%",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              textDecoration: "underline",
              mb: 2,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            Events
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={showPastEvents}
                onChange={() => {
                  setShowPastEvents((p) => !p);
                  setCurrentPage(0);
                }}
              />
            }
            label="Show Past Events"
          />

          <Box sx={{}}>
            <EventAccordion
              data={getPaginatedResults()}
              showIndicators={true}
              loading={eventsLoading || bandLoading}
              totalCount={totalItems}
              showCompactDesign={true}
              showImage={false}
              isMainView={false}
              setPaginationProps={(skip, take) => {
                setLocalPagination({
                  skip,
                  take,
                });
              }}
              currentPage={currentPage}
              setCurrentPage={(v) => setCurrentPage(v)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BandDetailsPage;
